/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { refreshTokens, getTokens } from 'src/features/Auth';
import { syncCompanyContext } from 'src/features/Auth/Auth.slice';
import { setMultiCompanyErrorState } from 'src/features/MultiCompanyAccess/MultiCompanyAccessErrorState';
import { store } from 'src/app/store';
import { formatTextForSentryLog } from 'src/utils/Formatter';

axios.defaults.baseURL = process.env.REACT_APP_BASE_API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

const MAX_REFRESH_RETRY_ATTEMPTS = 5;
let refreshRetryAttempts = 0;

// Redirect to the authentication error page
const redirectToAuthErrorPage = async () => {
  refreshRetryAttempts = 0;
  setMultiCompanyErrorState();
  window.location.hash = '/multi-company';
};

// Attempt to refresh the token
const attemptTokenRefresh = async (refreshToken: string): Promise<any> => {
  try {
    const newToken = await refreshTokens(refreshToken);
    refreshRetryAttempts = 0;

    return newToken;
  } catch (error) {
    refreshRetryAttempts += 1;

    if (refreshRetryAttempts >= MAX_REFRESH_RETRY_ATTEMPTS) {
      Sentry.captureException(
        formatTextForSentryLog(
          `refresh token failed ${MAX_REFRESH_RETRY_ATTEMPTS} times`
        )
      );
      redirectToAuthErrorPage();
      throw error;
    }

    return attemptTokenRefresh(refreshToken);
  }
};

/*
    Request interceptors
*/
axios.interceptors.request.use((config: any) => {
  const { token } = getTokens();

  if (!token) {
    return Promise.reject(new Error('Missing auth token'));
  }

  config.headers['x-AuthToken'] = token;
  return config;
});

/*
    Response interceptors
*/
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const { refreshToken } = getTokens();

    if (!refreshToken) {
      return redirectToAuthErrorPage().then(() => Promise.reject(error));
    }

    return attemptTokenRefresh(refreshToken).then((newToken: any) => {
      store.dispatch(syncCompanyContext());
      error.config.headers['x-AuthToken'] = newToken;

      return axios.request(error.config);
    });
  }
);

export const fetcher = (url: any) => axios.get(url).then((res) => res.data);

export default axios;
